import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Collapse,
  Flex,
  Select,
  SimpleGrid,
  SimpleGridProps,
  Space,
  Switch,
  TextInput,
  Textarea
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp, IconWand } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { CharacterCounter } from 'components/CharacterCounter';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { SelectItem, SelectItemProps } from 'components/SelectItem';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_BEHAVIOR_PROMPT_QUERY_KEY } from 'shared/constants/query-keys';
import { BehaviorPromptAction } from 'shared/constants/smart-assistant';
import { ConfigurationFormValues, SmartAssistantModel, SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { CUSTOM_PROMPT_MAX_LENGTH } from 'shared/utils/form-validators';
import { getModelItems } from 'shared/utils/smart-assistant';

const MAX_INPUT_WIDTH = 400;

interface ConfigurationSectionProps extends SimpleGridProps {
  form: UseFormReturnType<ConfigurationFormValues>;
  smartAssistantType: SmartAssistantType;
  forceAlignVertical?: boolean;
  voiceModelsOnly?: boolean;
}

export function ConfigurationSection({
  form,
  smartAssistantType,
  forceAlignVertical = false,
  voiceModelsOnly = false,
  ...simpleGridProps
}: ConfigurationSectionProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();
  const enableSpecialModels = subscription.product.enableSpecialModels;
  const [opened, { toggle }] = useDisclosure(false);

  const [modelItems, setModelItems] = useState<SelectItemProps[]>(getModelItems(enableSpecialModels, voiceModelsOnly));
  const [promptGeneratorInstructions, setPromptGeneratorInstructions] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);

  const generateBehaviorPromptMutation = useMutation({
    mutationFn: SmartAssistantService.generateBehaviorPrompt,
    mutationKey: [SMART_ASSISTANT_BEHAVIOR_PROMPT_QUERY_KEY],
    onSuccess: (prompt: string) => {
      setIsGeneratingPrompt(false);
      form.setFieldValue('customSystemPrompt', prompt);
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.PROMPT_GENERATED');
    },
    onError: () => {
      setIsGeneratingPrompt(false);
      NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.PROMPT_GENERATION_ERROR');
    }
  });

  useEffect(() => {
    setModelItems(getModelItems(enableSpecialModels, voiceModelsOnly));
  }, [i18n.language, enableSpecialModels, voiceModelsOnly]);

  const onChangeModel = (value: SmartAssistantModel) => {
    form.setFieldValue('model', value);
  };

  const onAnswerRestrictionChange = (value: string) => {
    if (value === 'yes') {
      form.setFieldValue('restrictAnswersToResourcesContent', true);
    } else {
      form.setFieldValue('restrictAnswersToResourcesContent', false);
      form.setFieldValue('restrictAnswersToResourcesContentResponse', '');
    }
  };

  const handleGeneratePrompt = () => {
    setIsGeneratingPrompt(true);
    generateBehaviorPromptMutation.mutate({
      smartAssistantType,
      action: form.values.customSystemPrompt ? BehaviorPromptAction.UPDATE : BehaviorPromptAction.CREATE,
      currentPrompt: form.values.customSystemPrompt,
      instruction: promptGeneratorInstructions
    });
  };

  return (
    <SimpleGrid cols={forceAlignVertical ? 1 : 2} breakpoints={[{ maxWidth: 'md', cols: 1 }]} {...simpleGridProps}>
      <Box>
        <Textarea
          label={
            <Flex direction="column" align="flex-start" w="100%">
              <Flex direction="row" align="center" justify="center">
                <InputLabelWithInfo
                  label={t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.TITLE')}
                  infoContentTranslationKey="SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.INFO"
                />
                <Button
                  variant="white"
                  size="xs"
                  onClick={toggle}
                  styles={{
                    rightIcon: {
                      marginLeft: '4px'
                    }
                  }}
                  rightIcon={opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}>
                  {t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.PROMPT_GENERATOR')}
                </Button>
              </Flex>
              <Collapse in={opened} transitionDuration={200} transitionTimingFunction="linear" w="100%">
                <Flex direction="row" align="center" mb={10} w="100%">
                  <TextInput
                    size="xs"
                    placeholder={
                      form.values.customSystemPrompt
                        ? t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.PLACEHOLDER_UPDATE_PROMPT')
                        : t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.PLACEHOLDER_NEW_PROMPT')
                    }
                    value={promptGeneratorInstructions}
                    onChange={(event) => setPromptGeneratorInstructions(event.currentTarget.value)}
                    style={{ flexGrow: 1, marginRight: '10px' }}
                    onFocus={(event) => event.target.select()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleGeneratePrompt();
                    }}
                  />
                  <Button
                    size="xs"
                    variant="white"
                    leftIcon={<IconWand />}
                    loading={isGeneratingPrompt}
                    onClick={handleGeneratePrompt}>
                    {form.values.customSystemPrompt
                      ? t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.UPDATE_EXISTING')
                      : t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.GENERATE_NEW')}
                  </Button>
                </Flex>
              </Collapse>
            </Flex>
          }
          styles={{
            label: {
              width: '100%'
            }
          }}
          minRows={16}
          maxRows={16}
          placeholder={t('SMART_ASSISTANT.CONFIGURATION_SECTION.BEHAVIOR.PLACEHOLDER')}
          {...form.getInputProps('customSystemPrompt')}
        />
        <CharacterCounter length={form.values.customSystemPrompt.length} maxLength={CUSTOM_PROMPT_MAX_LENGTH} />
      </Box>

      <Box>
        <Select
          maw={MAX_INPUT_WIDTH}
          maxDropdownHeight={350}
          label={
            <InputLabelWithInfo
              label={t('SMART_ASSISTANT.MODEL.TITLE')}
              infoContentTranslationKey="SMART_ASSISTANT.MODEL.INFO"
            />
          }
          placeholder={t('SMART_ASSISTANT.CONFIGURATION_SECTION.MODEL.PLACEHOLDER')}
          itemComponent={SelectItem}
          data={modelItems}
          error={form.errors.model}
          onChange={(value) => onChangeModel(value as SmartAssistantModel)}
          value={form.values.model}
        />

        <Space h="md" />
        <Flex direction="row" gap="md">
          <Select
            w={form.values.restrictAnswersToResourcesContent ? undefined : MAX_INPUT_WIDTH}
            label={
              <InputLabelWithInfo
                label={t('SMART_ASSISTANT.RESTRICT_TO_RESOURCES.TITLE')}
                infoContentTranslationKey="SMART_ASSISTANT.RESTRICT_TO_RESOURCES.INFO"
              />
            }
            data={[
              {
                value: 'no',
                label: t('APP.ACTIONS.NO')
              },
              {
                value: 'yes',
                label: t('APP.ACTIONS.YES')
              }
            ]}
            error={form.errors.restrictAnswersToResourcesContent}
            onChange={onAnswerRestrictionChange}
            value={form.values.restrictAnswersToResourcesContent ? 'yes' : 'no'}
          />

          {form.values.restrictAnswersToResourcesContent && (
            <TextInput
              w={MAX_INPUT_WIDTH}
              label={t('SMART_ASSISTANT.RESTRICT_TO_RESOURCES.OUT_OF_CONTEXT_RESPONSE.LABEL')}
              placeholder={t('SMART_ASSISTANT.RESTRICT_TO_RESOURCES.OUT_OF_CONTEXT_RESPONSE.PLACEHOLDER')}
              {...form.getInputProps('restrictAnswersToResourcesContentResponse')}
            />
          )}
        </Flex>

        {form.values.enabledEmoji !== undefined && (
          <>
            <Space h="md" />
            <Switch
              label={
                <InputLabelWithInfo
                  label={t('SMART_ASSISTANT.EMOJI.LABEL')}
                  infoContentTranslationKey="SMART_ASSISTANT.EMOJI.DESCRIPTION"
                />
              }
              checked={form.values.enabledEmoji}
              {...form.getInputProps('enabledEmoji')}
            />
          </>
        )}
      </Box>
    </SimpleGrid>
  );
}
